import { Link } from "@tanstack/react-router";
import { Moon, Sunrise } from "lucide-react";
import { calculatePrayerTimes } from "@/lib/prayer/calc";
import { tzOffsetHours } from "@/data/locations";
import { gregorianToHijri, HIJRI_MONTHS } from "@/lib/calendar/hijri";
import { displayLocationTitle } from "@/lib/prayer/copy";
import { PrayerPdfButton } from "@/components/PrayerPdfButton";
import { useDefaultLocation } from "@/lib/prayer/location-context";

type Mode = "day" | "month" | "year";

/**
 * Internal-link card linking from a calendar page (date/month/year) to the
 * matching jadwal sholat / imsakiyah page. Boosts pSEO and helps users who
 * land on `/2-mei-2026` discover the prayer schedule for that date.
 */
export function PrayerCrossLinkCard({
  mode, y, m, d,
}: { mode: Mode; y: number; m: number; d?: number }) {
  const loc = useDefaultLocation();
  const isKec = loc.kind === "kecamatan";
  const sholatHref = isKec
    ? `/jadwal-sholat/${loc.parentSlug}/${loc.slug}`
    : `/jadwal-sholat/${loc.slug}`;
  const imsakHref = isKec
    ? `/jadwal-imsak/${loc.parentSlug}/${loc.slug}`
    : `/jadwal-imsak/${loc.slug}`;

  let title = "";
  let preview: { subuh: string; maghrib: string } | null = null;

  if (mode === "day" && d) {
    title = `Jadwal Sholat ${d}/${m}/${y}`;
    const t = calculatePrayerTimes({ y, m, d, lat: loc.lat, lng: loc.lng, tzHours: tzOffsetHours(loc.tz) });
    preview = { subuh: t.subuh, maghrib: t.maghrib };
  } else if (mode === "month") {
    title = `Jadwal Sholat sebulan ${m}/${y}`;
  } else {
    title = `Jadwal Sholat & Imsakiyah ${y}`;
  }

  const hijri = d ? gregorianToHijri(y, m, d) : null;
  const hijriHref = hijri
    ? `/kalender-hijriah/${hijri.year}/${hijri.month}/${hijri.day}/`
    : null;

  const displayName = displayLocationTitle(loc);
  return (
    <section className="rounded-lg border border-prayer/30 bg-prayer/5 p-4">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-prayer">
        <Moon className="h-4 w-4" /> {title} di {displayName}
      </h2>
      {preview && (
        <p className="mb-3 text-xs text-muted-foreground">
          <Sunrise className="mr-1 inline h-3 w-3" /> Subuh{" "}
          <span className="font-mono font-semibold text-prayer">{preview.subuh}</span>
          {" "}· Maghrib{" "}
          <span className="font-mono font-semibold text-prayer">{preview.maghrib}</span>
          {hijri && (
            <>
              {" · "}
              <Link to={hijriHref! as never} className="text-prayer hover:underline">
                {hijri.day} {HIJRI_MONTHS[hijri.month - 1]} {hijri.year} H
              </Link>
            </>
          )}
        </p>
      )}
      <div className="flex flex-wrap gap-2 text-xs">
        <Link to={sholatHref as never} className="rounded-md bg-prayer px-3 py-1.5 font-medium text-prayer-foreground hover:bg-prayer/90">
          Jadwal Sholat {displayName} →
        </Link>
        <Link to={imsakHref as never} className="rounded-md border border-prayer/40 px-3 py-1.5 font-medium text-prayer hover:bg-prayer/10">
          Jadwal Imsakiyah →
        </Link>
        <Link to={"/jadwal-sholat/lokasi" as never} className="rounded-md border border-border px-3 py-1.5 hover:bg-accent">
          Ganti lokasi
        </Link>
        <PrayerPdfButton
          variant="sholat"
          location={loc}
          defaultMode={mode}
          year={y}
          month={m}
          day={d}
        />
      </div>
    </section>
  );
}